import { useState, useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Star, MapPin, Clock, Percent, Award, ExternalLink, Navigation, Loader2
} from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Custom marker icons
const createCustomIcon = (color, isFeatured = false) => {
  return L.divIcon({
    className: "custom-marker",
    html: `
      <div class="relative">
        <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 0C7.163 0 0 7.163 0 16C0 28 16 40 16 40C16 40 32 28 32 16C32 7.163 24.837 0 16 0Z" fill="${color}"/>
          <circle cx="16" cy="16" r="8" fill="white"/>
        </svg>
        ${isFeatured ? '<div class="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center"><span class="text-[8px]">★</span></div>' : ''}
      </div>
    `,
    iconSize: [32, 40],
    iconAnchor: [16, 40],
    popupAnchor: [0, -40],
  });
};

const defaultIcon = createCustomIcon("#6366F1");
const featuredIcon = createCustomIcon("#D4AF37", true);
const openIcon = createCustomIcon("#22C55E");

// Component to handle map events and bounds
function MapEventHandler({ onBoundsChange }) {
  const map = useMapEvents({
    moveend: () => {
      const bounds = map.getBounds();
      onBoundsChange({
        ne_lat: bounds.getNorthEast().lat,
        ne_lng: bounds.getNorthEast().lng,
        sw_lat: bounds.getSouthWest().lat,
        sw_lng: bounds.getSouthWest().lng,
      });
    },
  });
  return null;
}

// Component to center map on user location
function CenterOnLocation({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView([center.lat, center.lng], 12);
    }
  }, [center, map]);
  return null;
}

function StarRating({ rating }) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <Star
        key={i}
        className={`w-3 h-3 ${i <= rating ? "text-yellow-500 fill-yellow-500" : "text-gray-300"}`}
      />
    );
  }
  return <div className="flex items-center gap-0.5">{stars}</div>;
}

function BusinessPopup({ listing, onViewDetails }) {
  const openStatus = listing.open_status || {};
  
  return (
    <div className="min-w-[220px] p-1">
      <div className="flex items-start gap-2 mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-1">
            <h3 className="font-semibold text-sm text-gray-900 font-['Nunito']">
              {listing.name}
            </h3>
            {listing.is_featured && (
              <Award className="w-3.5 h-3.5 text-yellow-500" />
            )}
          </div>
          <p className="text-xs text-gray-500 font-['Nunito']">{listing.category}</p>
        </div>
      </div>
      
      {listing.address && (
        <div className="flex items-start gap-1.5 mb-2">
          <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5" />
          <span className="text-xs text-gray-600 font-['Nunito']">{listing.address}</span>
        </div>
      )}
      
      <div className="flex items-center justify-between mb-2">
        {listing.review_count > 0 ? (
          <div className="flex items-center gap-1">
            <StarRating rating={Math.round(listing.average_rating)} />
            <span className="text-xs text-gray-500 font-['Nunito']">
              ({listing.review_count})
            </span>
          </div>
        ) : (
          <span className="text-xs text-gray-400 font-['Nunito']">No reviews yet</span>
        )}
        
        {openStatus.is_open !== null && (
          <Badge 
            className={`text-[10px] ${openStatus.is_open ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
          >
            <Clock className="w-2.5 h-2.5 mr-1" />
            {openStatus.is_open ? "Open" : "Closed"}
          </Badge>
        )}
      </div>
      
      {listing.has_discounts && (
        <Badge className="mb-2 bg-purple-100 text-purple-700 text-[10px]">
          <Percent className="w-2.5 h-2.5 mr-1" />
          Parishioner Discount
        </Badge>
      )}
      
      <Button 
        size="sm" 
        className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white text-xs font-['Nunito']"
        onClick={() => onViewDetails(listing.listing_id)}
      >
        View Details
        <ExternalLink className="w-3 h-3 ml-1" />
      </Button>
    </div>
  );
}

export default function DirectoryMap({ 
  listings = [], 
  userLocation, 
  onBoundsChange,
  onViewDetails,
  loading = false 
}) {
  const [mapCenter, setMapCenter] = useState([39.8283, -98.5795]); // US center
  const [mapZoom, setMapZoom] = useState(4);

  useEffect(() => {
    if (userLocation) {
      setMapCenter([userLocation.lat, userLocation.lng]);
      setMapZoom(11);
    } else if (listings.length > 0) {
      // Center on first listing with coordinates
      const firstWithCoords = listings.find(l => l.coordinates);
      if (firstWithCoords) {
        setMapCenter([firstWithCoords.coordinates.lat, firstWithCoords.coordinates.lng]);
        setMapZoom(10);
      }
    }
  }, [userLocation, listings.length]);

  const markers = useMemo(() => {
    return listings
      .filter(listing => listing.coordinates)
      .map(listing => {
        const isOpen = listing.open_status?.is_open;
        let icon = defaultIcon;
        if (listing.is_featured) {
          icon = featuredIcon;
        } else if (isOpen) {
          icon = openIcon;
        }
        
        return {
          ...listing,
          icon,
          position: [listing.coordinates.lat, listing.coordinates.lng]
        };
      });
  }, [listings]);

  return (
    <div className="relative w-full h-[500px] md:h-[600px] rounded-lg overflow-hidden border border-[#E5E0D5]">
      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white/70 z-[1000] flex items-center justify-center">
          <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow">
            <Loader2 className="w-4 h-4 animate-spin text-[var(--liturgical-primary)]" />
            <span className="text-sm font-['Nunito'] text-gray-600">Loading businesses...</span>
          </div>
        </div>
      )}
      
      {/* Map Legend */}
      <div className="absolute top-3 right-3 z-[1000] bg-white rounded-lg shadow-md p-3 space-y-2">
        <p className="text-xs font-semibold text-gray-700 font-['Nunito']">Legend</p>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <span className="text-xs text-gray-600 font-['Nunito']">Featured</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="text-xs text-gray-600 font-['Nunito']">Open Now</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-indigo-500"></div>
          <span className="text-xs text-gray-600 font-['Nunito']">Business</span>
        </div>
      </div>
      
      {/* Business Count */}
      <div className="absolute bottom-3 left-3 z-[1000] bg-white rounded-lg shadow-md px-3 py-2">
        <p className="text-xs font-['Nunito'] text-gray-600">
          <span className="font-semibold text-gray-900">{markers.length}</span> businesses on map
        </p>
      </div>
      
      <MapContainer 
        center={mapCenter} 
        zoom={mapZoom} 
        className="w-full h-full"
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapEventHandler onBoundsChange={onBoundsChange} />
        
        {userLocation && (
          <CenterOnLocation center={userLocation} />
        )}
        
        {/* User Location Marker */}
        {userLocation && (
          <Marker 
            position={[userLocation.lat, userLocation.lng]}
            icon={L.divIcon({
              className: "user-location-marker",
              html: `
                <div class="relative">
                  <div class="w-6 h-6 rounded-full bg-blue-500 border-2 border-white shadow-lg flex items-center justify-center">
                    <div class="w-2 h-2 rounded-full bg-white"></div>
                  </div>
                  <div class="absolute -inset-2 rounded-full bg-blue-500/20 animate-ping"></div>
                </div>
              `,
              iconSize: [24, 24],
              iconAnchor: [12, 12],
            })}
          >
            <Popup>
              <div className="text-center p-1">
                <Navigation className="w-4 h-4 mx-auto text-blue-500 mb-1" />
                <p className="text-xs font-['Nunito'] font-semibold">Your Location</p>
              </div>
            </Popup>
          </Marker>
        )}
        
        {/* Business Markers */}
        {markers.map(listing => (
          <Marker
            key={listing.listing_id}
            position={listing.position}
            icon={listing.icon}
          >
            <Popup>
              <BusinessPopup 
                listing={listing} 
                onViewDetails={onViewDetails}
              />
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
